/**
 * DraftLoadingPlaceholder — replaces the static right-panel placeholder
 * during /v2 long-running drafts (gpt-5.5 NDA: 2-3 min).
 *
 * Shows:
 *   • Pulsing skeleton shimmer
 *   • Elapsed time counter (mm:ss)
 *   • A dynamic phase message (Arabic + English)
 *   • The most-recent reasoning step echoed at the bottom
 *
 * Active when:
 *   • Draft has been requested but `editorContent` is still empty
 *   • Streaming OR polling fallback is in progress
 */
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { MorphLoading } from '@/components/ui/MorphLoading';

const PHASE_MESSAGES_AR = [
  'جارٍ تحليل المتطلبات القانونية...',
  'مراجعة نظام المعاملات المدنية...',
  'صياغة البنود الأساسية...',
  'التحقق من الامتثال للأنظمة السعودية...',
  'جارٍ إكمال المسودة... قد يستغرق ذلك 2-3 دقائق',
];
const PHASE_MESSAGES_EN = [
  'Analyzing legal requirements...',
  'Cross-checking Saudi Civil Transactions Law...',
  'Drafting core clauses...',
  'Validating compliance with Saudi regulations...',
  'Finalizing the draft... this may take 2-3 minutes',
];

export function DraftLoadingPlaceholder({ isArabic, startedAt, lastStep }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsedSec = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));
  const phaseIdx = Math.min(Math.floor(elapsedSec / 30), 4);
  const phases = isArabic ? PHASE_MESSAGES_AR : PHASE_MESSAGES_EN;
  const phaseMessage = phases[phaseIdx];

  return (
    <div
      className="h-full w-full flex items-start justify-center px-8 py-12"
      data-testid="draft-loading-placeholder"
    >
      <div className="max-w-xl w-full flex flex-col gap-8">
        {/* Hero — morphing loader replaces the legacy mm:ss countdown. */}
        <div className="flex flex-col items-center gap-4 text-center">
          <MorphLoading size="lg" data-testid="draft-loading-morph" />
          <div className="text-sm hv-text-muted tracking-wider uppercase">
            {isArabic ? 'إنشاء المسودة' : 'Drafting'}
          </div>
          <div
            className="text-base font-medium hv-text"
            data-testid="draft-loading-working"
          >
            {isArabic ? 'نعمل على ذلك...' : 'Working on it...'}
          </div>
          <div
            className="hv-text-muted text-sm"
            data-testid="draft-loading-phase"
            key={phaseIdx} /* re-mount on phase change so transition fires */
            style={{ animation: 'fadeIn 400ms ease' }}
          >
            {phaseMessage}
          </div>
        </div>

        {/* Skeleton shimmer — gives the user a visual sense of "content
            forming". Six lines of varying widths in the document area. */}
        <div className="flex flex-col gap-3 w-full pt-4">
          {['85%', '92%', '78%', '95%', '70%', '88%'].map((w, i) => (
            <div
              key={i}
              className="h-3 rounded bg-black/[0.06] animate-pulse"
              style={{ width: w, animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>

        {/* Last reasoning step — echoes the most recent step from the
            heartbeat / agent timeline. Reassures the user that work is
            still progressing. */}
        {lastStep && (
          <div
            className="hv-border-t pt-4 text-xs hv-text-muted text-center"
            data-testid="draft-loading-last-step"
          >
            <span className="opacity-60 me-2">
              {isArabic ? 'آخر نشاط:' : 'last activity:'}
            </span>
            <span className="hv-text-subtle">{lastStep}</span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/**
 * DraftReadyToast — brief "✓ تم!" confirmation banner shown for ~2.5s
 * when a draft completes. Sits at the top of the right pane.
 */
export function DraftReadyToast({ isArabic, visible }) {
  if (!visible) return null;
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-4 py-2 text-sm font-medium',
        'bg-green-50 text-green-800 border-b border-green-200',
      )}
      style={{ animation: 'slideDown 300ms ease' }}
      data-testid="draft-ready-toast"
    >
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
        <path
          d="M2.5 8.5L6 12L13.5 4.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>
        {isArabic ? 'تم! المسودة جاهزة' : 'Done! Draft ready'}
      </span>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-100%); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default DraftLoadingPlaceholder;
